import { platformOptionalActionProviderHostCapability, platformOptionalFactProviderHostCapability, } from '@crest/platform-contracts/hosts';
import { defineCrestModule } from '@crest/platform-contracts/modules';
import { knowledgeAcknowledgementsCapability, knowledgeLibraryCapability } from './contracts/knowledge-library.contract.js';
import { knowledgeAccessPolicyCapability, knowledgeCollectionsCapability, knowledgeReviewCapability } from './contracts/knowledge-governance.contract.js';
import { knowledgeImportCapability } from './contracts/knowledge-import.contract.js';
import { knowledgePublishedArtifactCapability, knowledgePublishedArtifactProviderId, } from './contracts/knowledge-published-artifact.contract.js';
import { knowledgeDocumentsSignedPolicyAcknowledgementCapability, knowledgeDocumentsSignedPolicyAcknowledgementProviderId, knowledgeDocumentsSignedPolicyFactsCapability, knowledgeDocumentsSignedPolicyFactsProviderId, } from './contracts/knowledge-documents-signed-policy.contract.js';
import { knowledgeAssistantContextCapability, knowledgeAssistantContextProviderId, } from './contracts/knowledge-assistant-context.contract.js';
export const knowledgeModuleManifest = defineCrestModule({
    id: 'knowledge', version: '1.0.0', kind: 'business', required: false,
    requires: ['platform'],
    requiredCapabilities: [
        'platform.tenant-context.v1', 'platform.module-access.v1',
        'platform.membership-directory.v1', platformOptionalFactProviderHostCapability,
        platformOptionalActionProviderHostCapability,
    ],
    optionalCapabilities: [], optionalCapabilityFallbacks: [],
    provides: [knowledgeLibraryCapability, knowledgeAcknowledgementsCapability, knowledgePublishedArtifactCapability,
        knowledgeCollectionsCapability, knowledgeAccessPolicyCapability, knowledgeReviewCapability,
        knowledgeImportCapability, knowledgeDocumentsSignedPolicyFactsCapability,
        knowledgeDocumentsSignedPolicyAcknowledgementCapability,
        knowledgeAssistantContextCapability],
    permissions: [
        'knowledge.library.read', 'knowledge.library.manage', 'knowledge.library.publish',
        'knowledge.acknowledgements.create', 'knowledge.acknowledgements.read-all',
        'knowledge.published-artifacts.read',
        'knowledge.governance.read', 'knowledge.collections.manage', 'knowledge.access.manage',
        'knowledge.reviews.read', 'knowledge.reviews.read-all',
        'knowledge.reviews.assign', 'knowledge.reviews.decide',
        'knowledge.import.read', 'knowledge.import.execute', 'knowledge.import.rollback',
    ],
    settings: ['knowledge.library.configuration', 'knowledge.display.library', 'knowledge.copy.library',
        'knowledge.governance.configuration', 'knowledge.display.governance', 'knowledge.copy.governance'],
    copyCatalogs: ['knowledge.copy.library', 'knowledge.copy.governance'],
    events: {
        publishes: [
            'knowledge.document-created.v1', 'knowledge.version-created.v1',
            'knowledge.document-published.v1', 'knowledge.document-archived.v1',
            'knowledge.document-rolled-back.v1', 'knowledge.version-acknowledged.v1',
            'knowledge.signed-policy-acknowledged.v1',
            'knowledge.collection-created.v1', 'knowledge.collection-updated.v1',
            'knowledge.collection-archived.v1', 'knowledge.document-access-configured.v1',
            'knowledge.review-assigned.v1', 'knowledge.review-decided.v1',
        ],
        subscribes: [],
    },
    server: {
        routes: [
            { id: 'knowledge.routes.library', requiredPermission: 'knowledge.library.read', priority: 0 },
            { id: 'knowledge.routes.management', requiredPermission: 'knowledge.library.manage', priority: 10 },
            { id: 'knowledge.routes.acknowledgements', requiredPermission: 'knowledge.acknowledgements.create', priority: 20 },
            { id: 'knowledge.routes.governance', requiredPermission: 'knowledge.governance.read', priority: 30 },
        ],
        jobs: [], subscribers: [], slots: [{
                id: knowledgePublishedArtifactProviderId,
                requiredCapability: platformOptionalFactProviderHostCapability,
                requiredPermission: 'knowledge.published-artifacts.read', priority: 70,
            }, {
                id: knowledgeDocumentsSignedPolicyFactsProviderId,
                requiredCapability: platformOptionalFactProviderHostCapability,
                requiredPermission: 'knowledge.published-artifacts.read', priority: 80,
            }, {
                id: knowledgeDocumentsSignedPolicyAcknowledgementProviderId,
                requiredCapability: platformOptionalActionProviderHostCapability,
                requiredPermission: 'knowledge.acknowledgements.create', priority: 90,
            }, {
                id: knowledgeAssistantContextProviderId,
                requiredCapability: platformOptionalFactProviderHostCapability,
                requiredPermission: 'knowledge.library.read', priority: 100,
            }],
    },
    web: {
        routes: [
            { id: 'knowledge.web.library', requiredPermission: 'knowledge.library.read', priority: 0 },
            { id: 'knowledge.web.management', requiredPermission: 'knowledge.library.manage', priority: 10 },
            { id: 'knowledge.web.governance', requiredPermission: 'knowledge.governance.read', priority: 20 },
        ],
        navigation: [
            { id: 'knowledge.navigation.library', requiredPermission: 'knowledge.library.read', priority: 86 },
            { id: 'knowledge.navigation.management', requiredPermission: 'knowledge.library.manage', priority: 87 },
            { id: 'knowledge.navigation.governance', requiredPermission: 'knowledge.governance.read', priority: 88 },
        ], slots: [],
    },
    mobile: { routes: [], slots: [] }, portal: { routes: [], slots: [] },
    database: {
        schema: 'knowledge',
        owns: [
            'knowledge.documents', 'knowledge.document_versions', 'knowledge.acknowledgements',
            'knowledge.document_events', 'knowledge.commands', 'knowledge.collections',
            'knowledge.collection_access_rules', 'knowledge.document_access_rules',
            'knowledge.review_assignments', 'knowledge.review_decisions',
            'knowledge.governance_events', 'knowledge.governance_commands',
            'knowledge.import_runs', 'knowledge.import_identity_map',
            'knowledge.import_row_receipts', 'knowledge.import_rejects',
            'knowledge.import_rollback_receipts',
            'knowledge.acknowledge_signed_policy_v1',
        ],
        readsThrough: ['platform.membership-directory.v1'],
        migrations: ['knowledge.library-foundation', 'knowledge.access-structure-review-lifecycle',
            'knowledge.import-reconciliation'],
    },
    health: ['knowledge.database'],
    lifecycle: {
        initialization: { timeoutMs: 5_000, failureBehavior: 'quarantine-module' },
        resources: {
            requestConcurrency: 32, jobConcurrency: 0, queueBacklogLimit: 0,
            databaseConnectionBudget: 8, workerIsolation: 'shared-api',
        },
        disable: {
            data: 'retain', clientContributions: 'remove', serverRoutes: 'deny-tenant-access',
            scheduledJobs: 'skip',
            events: {
                behavior: 'drop-safe', durableCursor: false, retentionDays: 0, backlogLimit: 0,
                warningAt: 0, reconciliationProcedure: null,
                dropSafeReason: 'Knowledge owns no subscriber backlog in this foundation.',
            },
            outboundSideEffects: 'suppress-no-retroactive-send', inFlight: 'finish-idempotently',
        },
        resume: {
            configuration: 'last-compatible-published', healthChecks: ['knowledge.database'],
            reconciliationProcedure: null, visibility: 'after-health-and-reconciliation',
        },
        uninstall: {
            executableCode: 'remove', data: 'retain', tombstone: 'retain-immutable',
            destruction: 'separate-explicit-authority',
        },
    },
    retentionPolicy: 'Knowledge versions, acknowledgements, events, and command receipts remain immutable and retained while disabled.',
});
//# sourceMappingURL=knowledge.module.js.map